// src/components/ExpertSlider.jsx
import React, { useState, useEffect, useRef } from "react";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getTherapists } from "../../redux/therapistSlice";
import { getLanguages } from "../../redux/languageSlice";
import { capitalizeWords } from "../../utils/stringUtils";

// ExpertSlider props olarak id alabilir
const ExpertSlider = ({ id, bgColor = "bg-tertiary" }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // requestSentRef ile birden fazla istek gönderilmesini engelle
  const requestSentRef = useRef(false);
  const sliderViewportRef = useRef(null);
  const firstCardRef = useRef(null);
  
  // Redux'tan terapist verilerini al
  const { therapists, loading } = useSelector((state) => state.therapist);
  const languageState = useSelector((state) => state.languages);
  const fetchedLanguages = languageState?.fetchedLanguages || [];
  
  // Bileşen yüklendiğinde terapistleri getir (sadece bir kez)
  useEffect(() => {
    if (!requestSentRef.current && !therapists?.length) {
      dispatch(getTherapists());
      requestSentRef.current = true;
    }
    dispatch(getLanguages());
  }, [dispatch, therapists]);

  // Terapist detay sayfasında aktif terapisti hariç tut ve sadece onaylı olanları göster
  const filteredTherapists = therapists?.filter(therapist => {
    // Aktif terapisti hariç tut
    if (id && therapist._id === id) return false;
    
    // Onay durumunu kontrol et
    if (therapist.isActive === false) return false;
    return therapist.approvalStatus === 'approved' || therapist.status === 'approved' || therapist.isApprovedByAdmin === true || therapist.isApprovedByAdmin === 'true' || therapist.isVerified === true || therapist.isVerified === 'true';
  }) || [];
  const [currentIndex, setCurrentIndex] = useState(0);

  const FALLBACK_CARD_WIDTH = 280;
  const FALLBACK_CARD_GAP = 24;
  const [slideStep, setSlideStep] = useState(FALLBACK_CARD_WIDTH + FALLBACK_CARD_GAP);
  const [maxVisibleCards, setMaxVisibleCards] = useState(1);
  const [viewportOffset, setViewportOffset] = useState(180);

  useEffect(() => {
    const updateSliderMetrics = () => {
      const viewportWidth = sliderViewportRef.current?.clientWidth || 0;
      const cardEl = firstCardRef.current;

      let nextSlideStep = FALLBACK_CARD_WIDTH + FALLBACK_CARD_GAP;
      if (cardEl) {
        const cardStyle = window.getComputedStyle(cardEl);
        const marginRight = parseFloat(cardStyle.marginRight || "0");
        nextSlideStep = cardEl.getBoundingClientRect().width + marginRight;
      }

      setSlideStep(nextSlideStep);

      if (viewportWidth > 0) {
        const visible = Math.max(1, Math.floor(viewportWidth / nextSlideStep));
        setMaxVisibleCards(visible);
      } else {
        setMaxVisibleCards(1);
      }
    };

    updateSliderMetrics();
    // İlk render sonrası ölçümler kesinleşsin
    const rafId = requestAnimationFrame(updateSliderMetrics);
    const timeoutId = setTimeout(updateSliderMetrics, 200);

    const handleResize = () => {
      updateSliderMetrics();
      setViewportOffset(window.innerWidth < 640 ? 88 : 180);
    };
    setViewportOffset(window.innerWidth < 640 ? 88 : 180);
    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
      window.removeEventListener("resize", handleResize);
    };
  }, [filteredTherapists.length]);

  useEffect(() => {
    const maxIndex = Math.max(0, filteredTherapists.length - maxVisibleCards);
    if (currentIndex > maxIndex) {
      setCurrentIndex(maxIndex);
    }
  }, [currentIndex, filteredTherapists.length, maxVisibleCards]);

  const handlePrev = () => {
    if (filteredTherapists && filteredTherapists.length > 0) {
      const maxIndex = Math.max(0, filteredTherapists.length - maxVisibleCards);
      setCurrentIndex((prevIndex) => (prevIndex <= 0 ? maxIndex : prevIndex - 1));
    }
  };

  const handleNext = () => {
    if (filteredTherapists && filteredTherapists.length > 0) {
      const maxIndex = Math.max(0, filteredTherapists.length - maxVisibleCards);
      setCurrentIndex((prevIndex) => (prevIndex >= maxIndex ? 0 : prevIndex + 1));
    }
  };
  
  // Terapist detay sayfasına yönlendirme
  const handleTherapistDetail = (therapistId) => {
    navigate(`/therapistInfo/${therapistId}`);
  };

  // Convert HTML to plain text
  const getPlainText = (html) => {
    if (!html) return "";
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  // Eğer filtrelenmiş terapistler yüklenmemişse veya boşsa
  if (!filteredTherapists || filteredTherapists.length === 0) {
    if (loading) {
      return (
        <div id="about-us" className="bg-white text-center py-20">
          <div className={`relative ${bgColor} py-4 rounded-[50px] overflow-hidden mx-auto flex items-center justify-center`} style={{ height: "300px", width: "calc(100% - 45px)" }}>
            <div className="text-primary font-poppins text-lg">Therapists are loading...</div>
          </div>
        </div>
      );
    }
    return null; // Eğer terapistler yoksa ve yükleme tamamlandıysa, bileşeni gösterme
  }

  return (
    <div id="about-us" className="bg-white text-center py-8">
      {/* Başlıklar - sayfanın durumuna göre değişkenlik gösterir */}
      <div className="px-4 sm:px-6 md:px-10">
        {!location.pathname.includes("/therapistInfo") ? (
          // Ana sayfada gösterilecek başlıklar
          <>
            <h2 className="text-[18px] md:text-[32px] font-poppins leading-tight text-primary font-bold">
              {t("expertSliderTitle1")}
            </h2>
            <p className="text-[14px] md:text-[20px] leading-snug font-poppins text-primary font-bold mt-2">
              {t("expertSliderTitle2")}
            </p>
          </>
        ) : (
          // Terapist detay sayfasında gösterilecek başlık
          <h2 className="text-[24px] sm:text-[28px] md:text-[32px] font-poppins leading-tight text-primary font-bold mb-8">
            {t("similarTherapists")}
          </h2>
        )}
      </div>

      {/* Üst Kapsayıcı */}
      <div
        className={`relative ${bgColor} py-6 rounded-[50px] overflow-hidden mx-auto flex items-center`}
        style={{ minHeight: "380px", width: "calc(100% - 20px)" }}
      >
        {/* İç Kapsayıcı */}
        <div
          ref={sliderViewportRef}
          style={{
            flex: 1,
            width: `calc(100% - ${viewportOffset}px)`,
            margin: "0 auto",
            display: "flex",
            overflow: "hidden",
            alignSelf: "stretch",
          }}
        >
          {/* Slider İçeriği */}
          <div
            className="flex transition-transform duration-500 items-center"
            style={{
              transform: `translateX(-${currentIndex * slideStep}px)`,
              width: `${filteredTherapists.length * slideStep}px`,
            }}
          >
            {filteredTherapists.map((therapist, index) => (
              <div
                key={therapist._id}
                ref={index === 0 ? firstCardRef : null}
                className="flex-shrink-0 border-2 overflow-hidden rounded-[44.83px] shadow-md p-3 sm:p-4 bg-gradient-to-b from-[#FFFFFF] via-[#FFFFFF] to-[#D8E9E3] flex flex-col w-[220px] sm:w-[280px] md:w-[340px] h-full mr-4 sm:mr-6 last:mr-0"
              >
                {/* Üst Alan */}
                <div className="flex items-center">
                  <div className="w-[101px] h-[101px]">
                    {therapist.picture ? (
                      <img
                        src={therapist.picture}
                        alt={`${therapist.name} ${therapist.surname}`}
                        className="w-full h-full object-cover rounded-full border-[3px] border-cards-border"
                      />
                    ) : (
                      <div className="w-full h-full rounded-full bg-gray-200 flex items-center justify-center text-green-700 font-bold text-xl border-[3px] border-cards-border">
                        {therapist.name.charAt(0)}{therapist.surname.charAt(0)}
                      </div>
                    )}
                  </div>
                  <div className="ml-3 text-center flex-1 min-w-0">
                    <h3 className="w-full text-[12px] sm:text-[16px] md:text-[18px] font-poppins italic text-text truncate">
                      {capitalizeWords(therapist.name)} {therapist.surname}
                    </h3>
                    <p className="text-[9px] sm:text-[11px] md:text-[14px] text-gray-600 font-poppins italic ml-1">
                      {therapist.branchId?.name || t("expertClinicalPsychologist")}
                    </p>
                    <hr className="my-1 border-gray-300" />
                    <div className="flex items-center justify-center gap-1">
                      {Array(5)
                        .fill(0)
                        .map((_, i) => (
                          <svg
                            key={i}
                            xmlns="http://www.w3.org/2000/svg"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                            className="w-3 h-3 text-yellow-400"
                          >
                            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                          </svg>
                        ))}
                    </div>
                  </div>
                </div>

                {/* Orta Alan */}
                <div className="mt-8 w-full min-h-[110px]">
                  <p className="font-poppins italic text-[12px] sm:text-[16px] md:text-[20px] leading-[22px] sm:leading-[24px] text-text overflow-hidden"
                     style={{
                       display: '-webkit-box',
                       WebkitLineClamp: 4,
                       WebkitBoxOrient: 'vertical',
                     }}
                  >
                    {getPlainText(therapist.bio) || t("expertDescription")}
                  </p>
                </div>

                {/* Alt Alan */}
                <div className="flex justify-between mt-auto pt-4">
                  <div className="flex flex-col">
                    <span className="text-[10px] md:text-[12px] font-poppins font-bold text-primary px-1 uppercase mb-1">
                      {t("languages")}
                    </span>
                    <hr className="border-red-500 border-[2px] rounded mb-1" />
                    {therapist.languages && therapist.languages.length > 0 && (
                      <div className="flex flex-wrap gap-1 px-1">
                        {[...therapist.languages]
                          .sort((a, b) => {
                            const getCodeLabel = (lang) => {
                              const langId = typeof lang === 'object' ? lang._id : lang;
                              const found = fetchedLanguages.find(l => l._id === langId);
                              const code = (found?.code || (typeof lang === 'object' ? lang.code : '') || '').toUpperCase();
                              const name = (found?.name || (typeof lang === 'object' ? lang.name : '') || '').toUpperCase();
                              return { code, name };
                            };
                            const labelA = getCodeLabel(a);
                            const labelB = getCodeLabel(b);
                            const isEnA = labelA.code === 'EN' || labelA.name === 'ENGLISH';
                            const isEnB = labelB.code === 'EN' || labelB.name === 'ENGLISH';
                            if (isEnA && !isEnB) return -1;
                            if (!isEnA && isEnB) return 1;
                            return 0;
                          })
                          .map((langData, idx) => {
                            const langId = typeof langData === 'object' ? langData._id : langData;
                            const foundLang = fetchedLanguages.find(l => l._id === langId);
                            const displayLabel = foundLang 
                              ? (foundLang.code || foundLang.name) 
                              : (typeof langData === 'object' ? (langData.code || langData.name) : '...');
                            
                            return (
                              <span 
                                key={idx} 
                                className="text-[8px] md:text-[10px] font-poppins font-bold text-primary bg-tertiary px-1 rounded uppercase"
                              >
                                {displayLabel}
                              </span>
                            );
                          })}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleTherapistDetail(therapist._id)}
                    className="py-1 px-2 sm:px-4 w-[100px] sm:w-[140px] md:w-[200px] h-[34px] sm:h-[40px] md:h-[43px] rounded-full border-[3px] font-roboto text-[13px] sm:text-[16px] md:text-[20px] text-white transition-transform transform hover:scale-105 active:scale-95 duration-300 ease-in-out bg-button-bg border-selectedbackground"
                  >
                    {t("instantCall")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sol Ok */}
        <button
          onClick={handlePrev}
          className="absolute top-1/2 left-4 transform -translate-y-1/2 z-50 bg-button-bg border-[3px] border-white w-14 h-14 rounded-full flex items-center justify-center hover:bg-cards-border transition"
        >
          <ArrowBack style={{ fontSize: "32px", color: location.pathname.includes("/therapistInfo") ? "#D8E9E3" : "#ffffff" }} />
        </button>

        {/* Sağ Ok */}
        <button
          onClick={handleNext}
          className="absolute top-1/2 right-4 transform -translate-y-1/2 z-50 bg-button-bg border-[3px] border-white w-14 h-14 rounded-full flex items-center justify-center hover:bg-cards-border transition"
        >
          <ArrowForward style={{ fontSize: "32px", color: location.pathname.includes("/therapistInfo") ? "#D8E9E3" : "#ffffff" }} />
        </button>
      </div>
    </div>
  );
};

export default ExpertSlider;
